import React, { Suspense, lazy, useState } from "react";
import { formatCurrencySymbol } from "../../../shared/teamCurrencyFormatting";
import PropTypes from "prop-types";
import { Badge, Button, Plus, Select, Tabs, TextInput, Toggle, Trash01, Lock01, InfoCircle, LayersThree01 } from "@opendate-app/setlist";
import { useSeriesEditContext } from "../SeriesEditApp";
import { showTypesFor } from "../scheduleBuilder";
import {
  DEFAULT_LOCATION_REVEAL_MINUTES,
  MINUTES_PER_DAY,
  MINUTES_PER_HOUR,
  STATEMENT_DESCRIPTOR_DISALLOWED,
  STATEMENT_DESCRIPTOR_MAX_LENGTH,
  enabledConfirmationPageTemplateIds,
  newCustomCharge,
  nextSponsor,
  statementDescriptorProblem,
} from "../seriesSettings";
import SeriesFeeSettings from "./SeriesFeeSettings";
import VenueAgreementsEditor from "../../shared/components/VenueAgreementsEditor";
import MarketingPixelTagsInput from "../../shared/components/MarketingPixelTagsInput";
import TixelWaitlistRedirectToggle from "../../shared/components/TixelWaitlistRedirectToggle";
import XMarketingPixelsSettings from "../../ConfirmTicketing/components/XMarketingPixelsSettings";
import SponsorshipSettings from "../../../shared/SponsorshipSettings";
import { LOCATION_PRIVACY_OPTIONS } from "../../shared/constants/locationPrivacyOptions";

const CKEditorContainer = lazy(() => import("../../CKEditor/CKEditorContainer"));

export const SETTINGS_SECTIONS = [
  { key: "checkout", label: "Checkout & fees" },
  { key: "confirmation", label: "Confirmation" },
  { key: "pixels", label: "Marketing pixels" },
  { key: "location", label: "Location & waitlist" },
  { key: "payments", label: "Payments & tickets" },
];

const ALL_SHOW_TYPES = "__all";

const PIXEL_ROWS = [
  { kind: "google", key: "googleMarketingPixels", label: "Google Analytics", placeholder: "G-XXXXXXXXXX" },
  { kind: "facebook", key: "facebookMarketingPixels", label: "Meta Pixel" },
  { kind: "tiktok", key: "tiktokMarketingPixels", label: "TikTok Pixel" },
  { kind: "spotify", key: "spotifyMarketingPixels", label: "Spotify Pixel" },
];

const REVEAL_UNITS = [
  { value: "hours", label: "hours", minutes: MINUTES_PER_HOUR },
  { value: "days", label: "days", minutes: MINUTES_PER_DAY },
];

const checkedFrom = (value) => (value && value.target ? value.target.checked : Boolean(value));

const waitlistValue = (enabled) => (enabled == null ? "default" : enabled ? "on" : "off");

const datesLabel = (count) => (count > 0 ? `All ${count} dates` : "Every date");

const SettingsCard = ({ id, title, subtitle, datesBadge, children }) => (
  <section id={`series-settings-${id}`} className="series-settings-card" aria-labelledby={`series-settings-${id}-title`}>
    <header className="series-settings-card-header">
      <div>
        <h3 id={`series-settings-${id}-title`} className="series-settings-card-title" tabIndex={-1}>{title}</h3>
        <p className="series-edit-hint">{subtitle}</p>
      </div>
      <Badge size="sm">{datesBadge}</Badge>
    </header>
    <div className="series-settings-card-body">{children}</div>
  </section>
);

SettingsCard.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  datesBadge: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const SettingHeading = ({ title, hint, gate }) => (
  <div className="series-settings-heading">
    <div>
      <span className="series-settings-heading-title">{title}</span>
      {hint && <p className="series-edit-hint">{hint}</p>}
    </div>
    {gate && <Badge size="sm" color="purple">{gate}</Badge>}
  </div>
);

SettingHeading.propTypes = { title: PropTypes.string.isRequired, hint: PropTypes.node, gate: PropTypes.string };

const ShowTypeCopy = ({ showTypes, overrides, field, datesFor, renderEditor, seriesValue, customizeValue, onSeriesChange, onOverrideChange }) => {
  const [tab, setTab] = useState(ALL_SHOW_TYPES);
  const customized = showTypes.filter((showType) => overrides[showType] && overrides[showType][field] != null);
  const activeTab = showTypes.includes(tab) ? tab : ALL_SHOW_TYPES;

  if (showTypes.length < 2) return renderEditor(seriesValue, onSeriesChange, ALL_SHOW_TYPES);

  const override = activeTab === ALL_SHOW_TYPES ? null : overrides[activeTab] && overrides[activeTab][field];
  return (
    <>
      <Tabs
        variant="button-gray"
        size="sm"
        collapseBelow={0}
        aria-label="Show types"
        value={activeTab}
        onChange={setTab}
        items={[
          { value: ALL_SHOW_TYPES, label: "All show types" },
          ...showTypes.map((showType) => ({ value: showType, label: customized.includes(showType) ? `${showType} · custom` : showType })),
        ]}
      />
      {activeTab === ALL_SHOW_TYPES && (
        <>
          {renderEditor(seriesValue, onSeriesChange, ALL_SHOW_TYPES)}
          {customized.length > 0 && (
            <p className="series-edit-hint series-settings-inline-note">
              <InfoCircle width={16} height={16} aria-hidden="true" />
              {customized.join(", ")} dates use their own version. Changes here won't reach them.
            </p>
          )}
        </>
      )}
      {activeTab !== ALL_SHOW_TYPES && override == null && (
        <div className="series-settings-uses-default">
          <span>Uses the series default</span>
          <Button variant="secondary" size="sm" onClick={() => onOverrideChange(activeTab, customizeValue === undefined ? seriesValue : customizeValue)}>
            Customize for {activeTab}
          </Button>
        </div>
      )}
      {activeTab !== ALL_SHOW_TYPES && override != null && (
        <>
          <div className="series-settings-uses-default">
            <Badge size="sm">Only {datesFor(activeTab)} dates</Badge>
            <Button variant="link-color" size="sm" onClick={() => onOverrideChange(activeTab, null)}>
              Use the series default
            </Button>
          </div>
          {renderEditor(override, (value) => onOverrideChange(activeTab, value), activeTab)}
        </>
      )}
    </>
  );
};

ShowTypeCopy.propTypes = {
  showTypes: PropTypes.array.isRequired,
  overrides: PropTypes.object.isRequired,
  field: PropTypes.string.isRequired,
  datesFor: PropTypes.func.isRequired,
  renderEditor: PropTypes.func.isRequired,
  seriesValue: PropTypes.any,
  customizeValue: PropTypes.any,
  onSeriesChange: PropTypes.func.isRequired,
  onOverrideChange: PropTypes.func.isRequired,
};

const chargeUsable = (charge) => Boolean((charge.name || "").trim()) && parseFloat(String(charge.default_amount || "").replace(/,/g, "")) > 0;

const CustomCharges = ({ charges, currency, onChange }) => {
  const update = (index, changes) =>
    onChange(
      charges.map((charge, position) => {
        if (position !== index) return charge;
        const next = { ...charge, ...changes };
        return chargeUsable(next) ? next : { ...next, enabled: false };
      }),
    );

  return (
    <>
      {charges.map((charge, index) => (
        <div className="series-settings-row" key={charge.key}>
          <TextInput
            id={`series-${charge.key}-name`}
            label="Label"
            value={charge.name || ""}
            onChange={(e) => update(index, { name: e.target.value })}
          />
          <TextInput
            id={`series-${charge.key}-amount`}
            label="Amount"
            leadingText={formatCurrencySymbol(currency)}
            inputMode="decimal"
            value={charge.default_amount || ""}
            onChange={(e) => update(index, { default_amount: e.target.value })}
          />
          <Toggle
            id={`series-${charge.key}-enabled`}
            label="Enabled"
            checked={Boolean(charge.enabled)}
            disabled={!chargeUsable(charge)}
            onChange={(value) => update(index, { enabled: checkedFrom(value) })}
          />
          <Button
            variant="tertiary"
            size="sm"
            icon={Trash01}
            aria-label="Remove charge"
            onClick={() => onChange(charges.filter((_charge, position) => position !== index))}
          />
        </div>
      ))}
      <Button variant="link-color" size="sm" icon={Plus} onClick={() => onChange([...charges, newCustomCharge()])}>
        Add charge
      </Button>
    </>
  );
};

CustomCharges.propTypes = { charges: PropTypes.array.isRequired, currency: PropTypes.string, onChange: PropTypes.func.isRequired };

// Holds the typed number so the field can be empty mid-edit; minutes change only on a number.
const RevealAmount = ({ minutes, unitMinutes, onChange }) => {
  const [draft, setDraft] = useState(null);
  return (
    <TextInput
      id="series-location-reveal-amount"
      label="Reveal"
      type="number"
      min="0"
      value={draft === null ? String(Math.round(minutes / unitMinutes)) : draft}
      onChange={(e) => {
        const typed = e.target.value;
        setDraft(typed);
        const amount = parseInt(typed, 10);
        if (!Number.isNaN(amount)) onChange(Math.max(0, amount) * unitMinutes);
      }}
      onBlur={() => setDraft(null)}
    />
  );
};

RevealAmount.propTypes = { minutes: PropTypes.number.isRequired, unitMinutes: PropTypes.number.isRequired, onChange: PropTypes.func.isRequired };

const SeriesSettingsSection = () => {
  const {
    csrfToken, teamId, form, options, schedule, tickets, setTickets, settings, setSettings, selectedVenue, goToStep, isUploaded,
  } = useSeriesEditContext();
  const gates = options.seriesSettingGates || {};
  const templates = options.confirmationPageTemplates || [];
  const venueName = (selectedVenue && selectedVenue.label) || "the venue";
  const showTypes = showTypesFor(schedule);
  const dateCount = schedule.generated.length;
  const datesBadge = datesLabel(dateCount);
  const datesFor = (showType) => schedule.generated.filter((row) => (row.label || showTypes[0]) === showType).length;
  const patch = (changes) => setSettings((current) => ({ ...current, ...changes }));
  const patchOverride = (showType, field, value) =>
    setSettings((current) => ({
      ...current,
      byShowType: { ...current.byShowType, [showType]: { ...(current.byShowType[showType] || {}), [field]: value } },
    }));
  const enabledTemplateIds = enabledConfirmationPageTemplateIds(settings, templates);
  const descriptorProblem = statementDescriptorProblem(settings.statementDescriptorSuffix);
  const revealUnit = REVEAL_UNITS.find((unit) => unit.value === settings.locationRevealUnit) || REVEAL_UNITS[1];
  const revealMinutes = settings.locationRevealOffsetMinutes == null ? DEFAULT_LOCATION_REVEAL_MINUTES : settings.locationRevealOffsetMinutes;
  const [activeSection, setActiveSection] = useState(null);
  const scrollTo = (key) => {
    setActiveSection(key);
    const target = document.getElementById(`series-settings-${key}`);
    if (target && target.scrollIntoView) target.scrollIntoView({ behavior: "smooth", block: "start" });
    const heading = document.getElementById(`series-settings-${key}-title`);
    if (heading) heading.focus({ preventScroll: true });
  };

  return (
    <div className="series-edit-card series-settings">
      <h2 className="series-edit-section-title">Series Settings</h2>
      <p className="series-edit-section-subtitle">Event-level settings shared by every date in this series.</p>

      <div className="series-settings-layout">
        <aside className="series-settings-nav">
          <nav aria-label="Series settings sections">
            {SETTINGS_SECTIONS.map((section) => (
              <button
                type="button"
                key={section.key}
                className="series-settings-nav-item"
                aria-current={activeSection === section.key ? "true" : undefined}
                onClick={() => scrollTo(section.key)}
              >
                {section.label}
              </button>
            ))}
          </nav>
          <p className="series-edit-hint">
            Order limits, check-in and CAPTCHA are set for each show type in Ticket Config › Settings.
          </p>
        </aside>

        <div className="series-settings-sections">
          <div className="series-notice" role="note">
            <LayersThree01 width={20} height={20} aria-hidden="true" className="series-notice-icon" />
            <div>
              <div className="series-notice-title">These apply to every date in the series</div>
              <p className="series-notice-body">
                Including dates you add later.
                {showTypes.length > 1 && ` Confirmation copy and sponsorship can differ by show type (${showTypes.join(", ")}).`}
                {" "}To change one date, edit it from the events table.
              </p>
            </div>
          </div>

          <SettingsCard id="checkout" title="Checkout & fees" subtitle="What fans agree to and pay at checkout." datesBadge={datesBadge}>
            <SettingHeading title="Checkout agreements" hint="Fans must accept these before they can check out." />
            {((selectedVenue && selectedVenue.venueAgreements) || []).map((agreement) => (
              <div className="series-settings-venue-agreement" key={agreement.id}>
                <div>
                  <div dangerouslySetInnerHTML={{ __html: agreement.body }} />
                  <p className="series-edit-hint">Set on {venueName}. Applies to every event there.</p>
                </div>
                <Badge size="sm" icon={Lock01}>From venue</Badge>
              </div>
            ))}
            <VenueAgreementsEditor
              agreements={settings.plannedAgreements}
              onAgreementsChange={(plannedAgreements) => patch({ plannedAgreements })}
            />
            <SeriesFeeSettings
              csrfToken={csrfToken}
              teamId={teamId}
              venueOwnershipId={form.venueOwnershipId}
              fees={tickets.fees}
              onChange={(fees) => setTickets((current) => ({ ...current, fees }))}
              canAccessGeneralLedgerCodes={Boolean(options.canAccessGeneralLedgerCodes)}
              feeRuleSets={(selectedVenue && selectedVenue.feeRuleSets) || []}
            />
            {gates.chargesProcessingByTicket && (
              <>
                <SettingHeading
                  title="Custom charges & surcharges"
                  hint="Added to every purchase as its own line under fees at checkout."
                  gate="Charges by ticket"
                />
                <CustomCharges
                  charges={settings.plannedCustomCharges}
                  currency={options.baseCurrency}
                  onChange={(plannedCustomCharges) => patch({ plannedCustomCharges })}
                />
              </>
            )}
          </SettingsCard>

          <SettingsCard id="confirmation" title="Confirmation page & email" subtitle="What fans see right after they buy." datesBadge={datesBadge}>
            <SettingHeading title="Event details" hint={`Shown on the confirmation page and email, in addition to ${venueName}'s confirmation copy.`} />
            <ShowTypeCopy
              showTypes={showTypes}
              overrides={settings.byShowType}
              field="ticketConfirmationInfo"
              datesFor={datesFor}
              seriesValue={settings.ticketConfirmationInfo}
              onSeriesChange={(ticketConfirmationInfo) => patch({ ticketConfirmationInfo })}
              onOverrideChange={(showType, value) => patchOverride(showType, "ticketConfirmationInfo", value)}
              renderEditor={(content, setContent, tab) => (
                <Suspense fallback={null}>
                  <CKEditorContainer key={tab} content={content || ""} setContent={setContent} allowScriptEmbeds />
                </Suspense>
              )}
            />
            {templates.length > 0 && (
              <>
                <SettingHeading title="Templates" hint="Saved blocks from your ticketing settings. Turn on the ones that apply." />
                {templates.map((template) => (
                  <div className="series-settings-toggle-row" key={template.id}>
                    <Toggle
                      id={`series-confirmation-page-template-${template.id}`}
                      label={template.name}
                      checked={enabledTemplateIds.includes(template.id)}
                      onChange={(value) =>
                        patch({
                          confirmationPageTemplateIds: checkedFrom(value)
                            ? [...enabledTemplateIds, template.id]
                            : enabledTemplateIds.filter((id) => id !== template.id),
                        })
                      }
                    />
                  </div>
                ))}
              </>
            )}
          </SettingsCard>

          <SettingsCard
            id="pixels"
            title="Marketing pixels"
            subtitle={`Track checkout and purchases for this series in your ad accounts. ${venueName}'s venue pixels still fire on every date.`}
            datesBadge={datesBadge}
          >
            {PIXEL_ROWS.map((row) => (
              <div className="series-settings-pixel" key={row.kind}>
                <SettingHeading title={row.label} />
                <MarketingPixelTagsInput
                  kind={row.kind}
                  aria-label={`${row.label} IDs`}
                  placeholder={row.placeholder}
                  value={settings[row.key].map((value) => ({ value }))}
                  onChange={(pixels) => patch({ [row.key]: pixels.map((pixel) => pixel.value) })}
                />
              </div>
            ))}
            <SettingHeading title="X Pixel" hint="For each conversion you track, paste the event ID from X Events Manager." />
            <XMarketingPixelsSettings
              xMarketingPixels={settings.xMarketingPixels}
              setXMarketingPixels={(xMarketingPixels) => patch({ xMarketingPixels })}
            />
          </SettingsCard>

          <SettingsCard
            id="location"
            title="Location & waitlist"
            subtitle="How the location shows and what happens when a date sells out."
            datesBadge={datesBadge}
          >
            <SettingHeading
              title="Location privacy"
              hint="Hide the address for a secret location. Fans see a note that it will be announced before their date."
              gate="Location reveal"
            />
            <Select
              id="series-location-privacy"
              aria-label="Location privacy"
              width={360}
              options={LOCATION_PRIVACY_OPTIONS}
              disabled={!gates.locationRevealCampaignConfigured && settings.locationPrivacy === "visible"}
              value={settings.locationPrivacy}
              onChange={(locationPrivacy) =>
                patch({
                  locationPrivacy,
                  locationRevealOffsetMinutes: locationPrivacy === "visible" ? null : revealMinutes,
                })
              }
            />
            {!gates.locationRevealCampaignConfigured && settings.locationPrivacy === "visible" && (
              <p className="series-edit-hint">
                To hide a location you first need an active <strong>Event Location Revealed</strong> campaign, so ticket
                holders are sent the address when it is revealed.
              </p>
            )}
            {settings.locationPrivacy !== "visible" && (
              <div className="series-settings-row">
                <TextInput
                  id="series-secret-location-label"
                  label="Location label"
                  maxLength={100}
                  placeholder="Secret garden location"
                  value={settings.secretLocationLabel}
                  onChange={(e) => patch({ secretLocationLabel: e.target.value })}
                />
                <RevealAmount
                  minutes={revealMinutes}
                  unitMinutes={revealUnit.minutes}
                  onChange={(locationRevealOffsetMinutes) => patch({ locationRevealOffsetMinutes })}
                />
                <Select
                  id="series-location-reveal-unit"
                  label="Unit"
                  width={120}
                  options={REVEAL_UNITS.map(({ value, label }) => ({ value, label }))}
                  value={revealUnit.value}
                  onChange={(unit) => {
                    const minutes = REVEAL_UNITS.find((option) => option.value === unit).minutes;
                    patch({ locationRevealUnit: unit, locationRevealOffsetMinutes: Math.round(revealMinutes / revealUnit.minutes) * minutes });
                  }}
                />
                <span className="series-edit-hint">before each date</span>
              </div>
            )}

            <SettingHeading title="Sold-out waitlist" hint="When a date sells out, fans can join a waitlist instead of seeing a dead end." />
            <Select
              id="series-sold-out-waitlist"
              aria-label="Sold-out waitlist"
              width={360}
              options={[
                { value: "default", label: `Follow team default (${gates.collectWaitlistInterestDefault ? "on" : "off"})` },
                { value: "on", label: "On for this series" },
                { value: "off", label: "Off for this series" },
              ]}
              value={waitlistValue(settings.collectWaitlistInterestEnabled)}
              onChange={(value) => patch({ collectWaitlistInterestEnabled: value === "default" ? null : value === "on" })}
            />

            {gates.tixelEnabled && (
              <>
                <SettingHeading
                  title="Send sold-out fans to Tixel"
                  hint="Replace the waitlist form with a link to each date's Tixel resale page."
                  gate="Tixel"
                />
                <TixelWaitlistRedirectToggle
                  id="series-tixel-waitlist-redirect"
                  checked={settings.tixelWaitlistRedirectEnabled == null ? gates.tixelWaitlistRedirectDefault : settings.tixelWaitlistRedirectEnabled}
                  onChange={(tixelWaitlistRedirectEnabled) => patch({ tixelWaitlistRedirectEnabled })}
                />
              </>
            )}
          </SettingsCard>

          <SettingsCard
            id="payments"
            title="Payments & tickets"
            subtitle="How the charge appears and what's printed on digital tickets."
            datesBadge={datesBadge}
          >
            <SettingHeading
              title="Card statement descriptor"
              hint={`Appears on fans' card statements. Up to ${STATEMENT_DESCRIPTOR_MAX_LENGTH} characters after OD*.`}
            />
            <TextInput
              id="series-statement-descriptor-suffix"
              aria-label="Statement descriptor"
              leadingText="OD*"
              maxLength={STATEMENT_DESCRIPTOR_MAX_LENGTH}
              value={settings.statementDescriptorSuffix}
              error={descriptorProblem || undefined}
              hint={settings.statementDescriptorSuffix ? `Fans see: OD* ${settings.statementDescriptorSuffix}` : "Leave blank to use the venue and series name."}
              onChange={(e) => patch({ statementDescriptorSuffix: e.target.value.replace(STATEMENT_DESCRIPTOR_DISALLOWED, "") })}
            />
            <ShowTypeCopy
              showTypes={showTypes}
              overrides={settings.byShowType}
              field="sponsor"
              datesFor={datesFor}
              seriesValue={settings.sponsor}
              onSeriesChange={(sponsor) => patch({ sponsor })}
              customizeValue={{
                show_sponsor_on_digital_tickets: settings.sponsor.show_sponsor_on_digital_tickets,
                sponsor_name: settings.sponsor.sponsor_name,
              }}
              onOverrideChange={(showType, value) => patchOverride(showType, "sponsor", value)}
              renderEditor={(entity, setEntity, tab) => (
                <SponsorshipSettings
                  key={tab}
                  entity={entity}
                  uploaded={Boolean(isUploaded && isUploaded(entity.sponsor_logo))}
                  onEntityChange={(next) => setEntity(nextSponsor(entity, next))}
                />
              )}
            />
          </SettingsCard>

          <div className="series-step-footer">
            <Button variant="secondary" onClick={() => goToStep("details")}>Back to setup</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeriesSettingsSection;
